import React, {createRef, useState, useContext} from 'react';
import {useQueryClient} from "react-query";
import {useAuth0} from "@auth0/auth0-react";
import moment from "moment";
import MaterialTable from "@material-table/core";
import { TablePagination, useTheme, } from "@material-ui/core";
import SystemContext from "../../../SystemContext";
import { ValidatorColumn, ColumnWidth } from "../../../blocks/ColumnHelper";
import UldTypeContoursValidator from "../validators/UldTypeContoursValidator";

import { useUldTypeContours, useAddUldTypeContour, useUpdateUldTypeContour, useDeleteUldTypeContour } from "../../../services/settings";
import { 
  MaskedText, 
  MaskedNumber,
  ComboSelect,
} from "../../../controls";

const UldTypeContourList = (_props) => {
  const {isAuthenticated} = useAuth0();
  const SystemData = useContext(SystemContext);
  const theme = useTheme();
  const validatorColumn = ValidatorColumn(UldTypeContoursValidator, true);

  const props = {
    ..._props
  }
  const [state, setState] = useState({
    canEdit: props.canEdit,
    activeRow: null
  });

  const tableRef = createRef({state: {lastEditingRow: false}});

  const handleAddUldTypeContour = useAddUldTypeContour();
  const handleUpdateUldTypeContour = useUpdateUldTypeContour();
  const handleDeleteUldTypeContour = useDeleteUldTypeContour();

  const queryClient = useQueryClient();
  const extraKeys = [];

  const uldTypeContours = useUldTypeContours({tableRef, queryClient, extraKeys});

  const getColumns = (props) => { 
    const baseColumn = {
      cellStyle: { fontSize:14, padding: "8px 2px" },
      headerStyle: { padding: "8px 2px" },
      editable: props.canEdit ? "always" : "never",
      emptyValue: "",
      initialEditValue: "",
    };
    const formatChars={ 
      "U": "[A-Z]", 
      "A": "[A-Z0-9]", 
      "a": "[A-Za-z0-9]",
      "i": "[A-Za-z0-9\\-]",
      "9": "[0-9]",
      "D": "[0-9.]",
      "-": "-",
      "x": "."
    }
    const defaultEditProps = {
      style:{fontSize: "0.875rem"}
    };

    return [
      {...validatorColumn},
      /*
        Can use ...ColumnWidth(70) to generate a fixed width column
      */

      {
        ...baseColumn,
        ...ColumnWidth(120),
        title: "ULD Type",
        field: "uldType",
        editable: state.canEdit ? "onAdd" : "never",
        initialEditValue: "",
        render: (rowData) => (
          <ComboSelect
            id="uldType"
            group="ULD TYPES"
            fullWidth
            value={rowData.uldType}
            displayOnly={true}
          />
        ),
        editComponent: (props) => (
          <ComboSelect
            id="uldType"
            group="ULD TYPES"
            fullWidth
            value={props.value}
            onChange={props.onChange}
          />
        )
      },
      {
        ...baseColumn,
        ...ColumnWidth(120),
        title: "Contour Code",
        field: "contourCode",
        initialEditValue: "",
        render: (rowData) => (
          <ComboSelect
            id="contourCode"
            group="CONTOUR CODES"
            fullWidth
            value={rowData.contourCode}
            displayOnly={true}
          />
        ),
        editComponent: (props) => (
          <ComboSelect
            id="contourCode"
            group="CONTOUR CODES"
            fullWidth
            value={props.value}
            onChange={props.onChange}
          />
        )
      },
      {
        ...baseColumn,
        ...ColumnWidth(70),
        title: "",
      },
      {
        ...baseColumn,
        ...ColumnWidth(120),
        title: "Length",
        field: "length",
        type: "numeric",
        emptyValue: null,
        initialEditValue: null,  
        editComponent: (props) => (
          <MaskedNumber
            format={"#".repeat(5)}
            value={props.value}
            decimalScale={0}
            onChange={props.onChange}
            style={{ fontSize: "0.875rem", textAlign: "right" }}
          />
        ),
        render: (props) => (
          <MaskedNumber
            displayType="text"
            value={props.length}
            decimalScale={0}
          />
        )
      },
      {
        ...baseColumn,
        ...ColumnWidth(120),
        title: "Width",
        field: "width",
        type: "numeric",
        emptyValue: null,
        initialEditValue: null,  
        editComponent: (props) => (
          <MaskedNumber
            format={"#".repeat(5)}
            value={props.value}
            decimalScale={0}
            onChange={props.onChange}
            style={{ fontSize: "0.875rem", textAlign: "right" }}
          />
        ),
        render: (props) => (
          <MaskedNumber
            displayType="text"
            value={props.width}
            decimalScale={0}
          />
        )
      },
      {
        ...baseColumn,
        ...ColumnWidth(120),
        title: "Height",
        field: "height",
        type: "numeric",
        emptyValue: null,
        initialEditValue: null,  
        editComponent: (props) => (
          <MaskedNumber
            format={"#".repeat(5)}
            value={props.value}
            decimalScale={0}
            onChange={props.onChange}
            style={{ fontSize: "0.875rem", textAlign: "right" }}
          />
        ),
        render: (props) => (
          <MaskedNumber
            displayType="text"
            value={props.height}
            decimalScale={0}
          />
        )
      },
      {
        ...baseColumn,
        ...ColumnWidth(70),
        title: "",
      },
      {
        ...baseColumn,
        ...ColumnWidth(150),
        title: "Max Weight",
        field: "maxWeight",
        type: "numeric",
        emptyValue: null,
        initialEditValue: null,  
        editComponent: (props) => (
          <MaskedNumber
            format={"#".repeat(5)}
            value={props.value}
            decimalScale={0}
            onChange={props.onChange}
            style={{ fontSize: "0.875rem", textAlign: "right" }}
          />
        ),
        render: (props) => (
          <MaskedNumber
            displayType="text"
            value={props.maxWeight}
            decimalScale={0}
          />
        )
      },
      {
        ...baseColumn,
        ...ColumnWidth(200),
        title: "Standard Pivot Weight",
        field: "standardPivotWeight",
        type: "numeric",
        emptyValue: null,
        initialEditValue: null,  
        editComponent: (props) => (
          <MaskedNumber
            format={"#".repeat(5)}
            value={props.value}
            decimalScale={0}
            onChange={props.onChange}
            style={{ fontSize: "0.875rem", textAlign: "right" }}
          />
        ),
        render: (props) => (
          <MaskedNumber
            displayType="text"
            value={props.standardPivotWeight}
            decimalScale={0}
          />
        )
      },
      {
        ...baseColumn,
        title: "",
      },
    ];
  };
  
  return (isAuthenticated &&
      <div style={{width: "100%", marginLeft: 20, marginRight: 20}}>
        <MaterialTable
            tableRef={tableRef}
            style={{
              border: "none",
              boxShadow: "none",
              borderRadius: 0,
            }}
            options={{
              actionsColumnIndex: -1,
              cellStyle: {
                padding: "8px 2px",
              },
              headerStyle: {
                backgroundColor: theme.palette.tableHeader.backgroundColor,
                color: theme.palette.tableHeader.textColor,
                padding: "2px",
              },
              toolbarButtonAlignment: "right",
              pageSize: 10,
              detailPanelType: "single",
            }}
            components={{
              Pagination: (props) => (
                  <TablePagination
                      {...props}
                      rowsPerPageOptions={[5, 10, 15]}
                  />
              ),
            }}
            title="Uld Type Contour Management"
            columns={getColumns({canEdit: state.canEdit})}
            data={uldTypeContours.data}
            editable={{
              isEditable: () => {
                return state.canEdit;
              },
              isDeletable: () => {
                return state.canEdit;
              },
              onRowAdd: state.canEdit
                  ? (newData) => new Promise((resolve, reject) => {
                    handleAddUldTypeContour.mutate({record: newData, resolve, reject, extraKeys});
                  })
                  : undefined,
              onRowUpdate: (newData, oldData) =>
                  new Promise((resolve, reject) => {
                    handleUpdateUldTypeContour.mutate({record: newData, oldRecord: oldData, resolve, reject, extraKeys});
                  }),
              onRowDelete: (oldData) =>
                  new Promise((resolve, reject) => {
                    handleDeleteUldTypeContour.mutate({oldRecord: oldData, resolve, reject, extraKeys});
                  }),
            }}
        />
      </div>
  );

}

export default UldTypeContourList;
